import { BasePanelComponent } from './BasePanel';
import { CssVisibilityConst, CssDisplayConst } from '../../Enum/CssStringConst';
import { AppViewReceiverModel } from 'src/app/Util/AppReceiver/AppViewReceiverModel';
import { AppViewReceiverUtil } from 'src/app/Util/AppReceiver/AppViewReceiverUtil';
import { Renderer2, ElementRef } from '@angular/core';

/**
 * BasePanelSlide
 *
 * Tabの下でスライドするコンポーネント用のベースクラス
 */
export abstract class BasePanelSlide extends BasePanelComponent {
	// コンポーネントとコントローラーの結合用プロパティは、継承関係上の上書きする。
	// F12での参照が出来うようにする為と、クラス構造で、どのコントローラーと紐づくか明示する為。
	public combineCtrl: any;

	// アニメーションの起点となる、イベントを処理する場所(コンポーネント)を指定しておく
	// アニメーション終了時の処理で、画面閉じる時に、その場所に全部閉じるイベントを送信する。
	public animationParentEventModel: AppViewReceiverModel;

	// スライドの下になったら消える用フラグ。継承先のHTMLでバインドして置くこと
	public display = CssDisplayConst.TABLE;
	public visibility = CssVisibilityConst.VISIBLE;

	// ジェスチャー対象のElementObject
	public gesturePage: any;
	// ジェスチャーアニメーションに必要なレンダー
	public renderer: Renderer2;

	/**
	 * constructor
	 */
	constructor() {
		super();
	}

	/**
	 * closeSelfView
	 * アニメーションして閉じる。slideOutが終わるとonAnimationEventDoneがコールバックされる。
	 * 外から呼ぶ用のメソッド。これで、一括閉じる時に、一緒に閉じてもらえる。
	 */
	public async closeSelfView() {

		this.log('closeSelfView');

		// 親に自分を表示した時に、下にいた消したやつを再表示してもらう
		this.animationParentEventModel.methodName = this.animationParentEventModel.className.VisiblePanelView;
		this.animationParentEventModel.instance = this; // 中で自分を探すので、自分を入れておく必要ある。
		await AppViewReceiverUtil.execEventProc(this.animationParentEventModel, CssDisplayConst.TABLE);

		// 外から呼ぶ用のメソッド。これで、一括閉じる時に、一緒に閉じてもらえる。
		// スライドアウトする。自分でやる
		this.slideOut(); // アニメーションが終了すると、onAnimationEventDoneが実行される。
	}

	/**
	 * onAnimationEventDone
	 * アニメーション用のDivから、アニメーション終了時に呼ばれる。
	 * @param $event アニメーションのインスタンス
	 */
	public async onAnimationEventDone($event) {

		this.log('onAnimationEventDone');
		this.log(this.animationSlideState);

		// アニメーション用モデルが無ければやらない
		if (!this.animationParentEventModel) {
			return;
		}

		// アニメーション終了は、In/Outで、それぞれきてしまう。閉じた時だけインスタンスを消す。
		if (this.animationSlideState === 'slideIn') {
			// スライドINしたら、下にいたやつを消してもらう
			this.animationParentEventModel.methodName = this.animationParentEventModel.className.VisiblePanelView;
			this.animationParentEventModel.instance = this; // 中で自分を探すので、自分を入れておく必要ある。
			await AppViewReceiverUtil.execEventProc(this.animationParentEventModel, CssDisplayConst.NONE);
			return;
		} else {
			// In でなければ Out としておく
			// 自分を閉じて欲しいので、親にCloseを伝達する。
			this.animationParentEventModel.methodName = this.animationParentEventModel.className.CloseMovePanelView;
			this.animationParentEventModel.instance = this; // 中で自分を探すので、自分を入れておく必要ある。
			await AppViewReceiverUtil.execEventProc(this.animationParentEventModel, null);
			return;
		}
	}

	/**
	 * setExecuteProc
	 * 時間がかかる処理のセット実行
	 * @param func 実行する処理
	 * @param param パラメータ
	 * @param showNotFound 404 をエラーとするか
	 */
	public async setExecuteProc(func: any, param: any, showNotFound = false): Promise<any> {
		// セットで実行
		const lst = await this.execFuncWithErrorHandling(this, func, param, showNotFound);

		// 失敗なら閉じて抜ける
		if (!lst) {
			this.closeSelfView();
			return null;
		}

		return lst;
	}


	/**
	 * setGestureTargetElementRef
	 * ジェスチャーを適用する対象をベースに保持する
	 * ジェスチャー使用時はHTMLの対象タグにイベントをバインドする。
	 * イベントはHammerjsに依存する
	 * @param elementRef ジェスチャー対象のElement参照
	 */
	public setGestureTargetElementRef(elementRef: ElementRef) {
		this.gesturePage = elementRef;
	}

	// スワイプ開始時の位置
	private startGestureX: number;
	/**
	 * onPanStart
	 * スワイプ開始時の処理
	 * 開始位置を保持しておいて、スワイプのアニメーションと完了時の処理を行うかに使用する
	 * @param ev ジェスチャーイベント
	 */
	public async onPanStart(ev: any) {
		this.startGestureX = ev.center.x;
	}

	/**
	 * onPanProgress
	 * PCでのスワイプジェスチャー処理
	 * 左から右にドラッグしたときに実行される
	 * @param ev ジェスチャーイベント
	 */
	public onPanProgress(ev: any) {

		// スワイプ開始時の位置が左から100px以上の場合は何もしない
		if (this.startGestureX > 100) { return; }

		// マイナス方向に動した場合、元の位置からの移動量が０未満になるときは0に補正する
		if (ev.deltaX < 0) { ev.deltaX = 0; }
		// パネルが移動するアニメーションを実装
		this.renderer.setStyle(this.gesturePage.nativeElement, 'transform', `translateX(${ev.deltaX}px)`);

	}

	/**
	 * onPanEnd
	 * PCでのスワイプジェスチャーが終わったときの処理
	 * スワイプから指（マウス）を離すと実行される
	 * @param ev ジェスチャーイベント
	 */
	public async onPanEnd(ev: any) {

		// スワイプ開始時の位置が左から100px以上の場合は何もしない
		if (this.startGestureX > 100) { return; }

		if (ev.deltaX >= 100) {
			// 左へのスワイプ移動量が100pxを超えたらパネルを閉じる
			await this.closeSelfView();
		}
		if (ev.deltaX < 100) {
			// スワイプ量が既定値以下ならもとに戻す
			this.renderer.setStyle(this.gesturePage.nativeElement, 'transform', 'translateX(0%)');
		}
	}
}
